import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { generalLedgerService, TrialBalanceRowDTO } from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, Calculator } from 'lucide-react';

const TrialBalancePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [rows, setRows] = useState<TrialBalanceRowDTO[]>([]);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);

  const load = (date: string) => {
    setLoading(true);
    generalLedgerService.getTrialBalance(date)
      .then((res) => {
        const data = (res?.data as any)?.data ?? res?.data ?? [];
        setRows(Array.isArray(data) ? data : []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(asOfDate); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
  const totalCredit = rows.reduce((s, r) => s + r.credit, 0);

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 900, margin: '0 auto' }}>
        <div className="ims-top-bar" style={{ marginBottom: 20 }}>
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={13} /> {t('common.back')}
            </button>
            <div>
              <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calculator size={18} style={{ color: 'var(--ims-accent)' }} />
                {t('accounting.trialBalance.title')}
              </h1>
              <p className="ims-page-subtitle">{t('accounting.trialBalance.subtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <input type="date" className="ims-form-control" value={asOfDate}
              onChange={(e) => { setAsOfDate(e.target.value); load(e.target.value); }} />
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : rows.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            {t('accounting.trialBalance.noActivity')}
          </div>
        ) : (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th" style={{ width: 90 }}>{t('accounting.coa.code')}</th>
                    <th className="ims-th">{t('accounting.coa.name')}</th>
                    <th className="ims-th" style={{ width: 110 }}>{t('accounting.coa.accountType')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 140 }}>{t('accounting.ledger.debit')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 140 }}>{t('accounting.ledger.credit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.accountCode} className="ims-tr">
                      <td className="ims-td"><code>{r.accountCode}</code></td>
                      <td className="ims-td">{r.accountName}</td>
                      <td className="ims-td">{t(`accounting.accountTypes.${r.accountType}`)}</td>
                      <td className="ims-td ims-td--line-total">{r.debit > 0 ? fmt(r.debit) : ''}</td>
                      <td className="ims-td ims-td--line-total">{r.credit > 0 ? fmt(r.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ims-td" colSpan={3}><strong>{t('accounting.gl.total')}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totalDebit)}</strong></td>
                    <td className="ims-td ims-td--line-total"><strong>{fmt(totalCredit)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {totalDebit !== totalCredit && (
              <div style={{ padding: '10px 16px', color: 'var(--ims-red-text)', fontSize: 13 }}>
                {t('accounting.trialBalance.outOfBalance')}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrialBalancePage;
